import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import type {
  ColDef,
  GridReadyEvent,
  SelectionChangedEvent
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  FiActivity,
  FiChevronDown,
  FiExternalLink,
  FiLink,
  FiPlus,
  FiRefreshCw,
  FiPackage,
  FiTrendingUp
} from "react-icons/fi";
import { RiAdvertisementLine } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { mockApi } from "../api/mockApi";

// Import AG Grid Enterprise and register modules
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  AllEnterpriseModule,
  IntegratedChartsModule
} from "ag-grid-enterprise";

// Register AG Grid modules including charts
ModuleRegistry.registerModules([
  AllCommunityModule,
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

function Links() {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showMainBulkMenu, setShowMainBulkMenu] = useState(false);

  const {
    data: links = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["links"],
    queryFn: mockApi.getLinks
  });

  // AG Grid column definitions
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "customId",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 100,
        maxWidth: 150,
        valueGetter: (params: any) => {
          return params.data?.customId || params.data?.id || "";
        },
        valueFormatter: (params: any) => {
          return params.value || "No ID";
        }
      },
      {
        headerName: "Domain",
        field: "domain",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 180,
        valueFormatter: (params: any) => {
          return params.value || "try.zown.ca";
        }
      },
      {
        headerName: "Platform",
        field: "platform",
        filter: "agSetColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 180,
        valueFormatter: (params: any) => {
          return params.value || "No Platform";
        }
      },
      {
        headerName: "Campaign Name",
        field: "linkName",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 150,
        maxWidth: 250,
        valueFormatter: (params: any) => {
          return params.value || "No Campaign";
        }
      },
      {
        headerName: "Ad Set Name",
        field: "adSetName",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 220,
        valueFormatter: (params: any) => {
          return params.value || "-";
        }
      },
      {
        headerName: "Ad Name",
        field: "adName",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 140,
        maxWidth: 220,
        valueFormatter: (params: any) => {
          return params.value || "-";
        }
      },
      {
        headerName: "Website Path",
        field: "destinationPath",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 150,
        maxWidth: 250,
        valueGetter: (params: any) => {
          const data = params.data;
          if (data?.destinationType === "single") {
            return data.destinationPath;
          } else if (data?.destinationType === "ab-test" && data?.abTestPaths?.length > 0) {
            const paths = data.abTestPaths
              .filter((p: any) => p.type === "path" && p.value)
              .map((p: any) => p.value);
            return paths.length > 0 ? paths.join(", ") : "-";
          } else if (data?.destinationType === "custom") {
            return "Custom URL";
          }
          return data?.destinationPath || "-";
        },
        valueFormatter: (params: any) => {
          return params.value || "-";
        }
      },
      {
        headerName: "Variant ID",
        field: "destinationVariant",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 120,
        maxWidth: 180,
        valueGetter: (params: any) => {
          const data = params.data;
          if (data?.destinationType === "variant") {
            return data.destinationVariant;
          } else if (data?.destinationType === "ab-test" && data?.abTestPaths?.length > 0) {
            const variants = data.abTestPaths
              .filter((p: any) => p.type === "variant" && p.value)
              .map((p: any) => p.value);
            return variants.length > 0 ? variants.join(", ") : "-";
          }
          return "-";
        },
        valueFormatter: (params: any) => {
          return params.value || "-";
        }
      },
      {
        headerName: "Redirect URL",
        field: "customRedirectUrl",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 200,
        flex: 1,
        valueGetter: (params: any) => {
          const data = params.data;
          if (data?.destinationType === "custom" && data?.customRedirectUrl) {
            return data.customRedirectUrl;
          }
          return "-";
        },
        valueFormatter: (params: any) => {
          if (params.value && params.value !== "-") {
            // Truncate long URLs for display
            if (params.value.length > 50) {
              return params.value.substring(0, 47) + "...";
            }
            return params.value;
          }
          return "-";
        },
        tooltip: true
      },
      {
        headerName: "Created",
        field: "created",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 220,
        maxWidth: 280,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          const date = new Date(params.value);
          // Format: Tue, 12 May 2020 23:50:21 EST
          const options: Intl.DateTimeFormatOptions = {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short',
            hour12: false
          };
          return date.toLocaleString('en-US', options).replace(',', ',');
        }
      },
      {
        headerName: "Updated",
        field: "updated",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        minWidth: 220,
        maxWidth: 280,
        valueFormatter: (params) => {
          if (!params.value) return "Never";
          const date = new Date(params.value);
          // Format: Tue, 12 May 2020 23:50:21 EST
          const options: Intl.DateTimeFormatOptions = {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short',
            hour12: false
          };
          return date.toLocaleString('en-US', options).replace(',', ',');
        }
      }
    ],
    []
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      suppressSizeToFit: true,
      autoHeaderHeight: true
    }),
    []
  );

  // Grid ready event
  const onGridReady = useCallback((event: GridReadyEvent) => {
    console.log("AG Grid is ready!", event.api);
    // Auto-size all columns based on content
    event.api.autoSizeAllColumns();
  }, []);

  // Handle selection changes
  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    setSelectedRows(selectedData);
  }, []);

  if (isLoading) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Container maxW="7xl" py={8}>
          <VStack gap={8} align="stretch">
            {/* Header Skeleton - matches actual header with buttons */}
            <Flex justify="space-between" align="center">
              <Box />
              <HStack gap={3}>
                {/* Bulk Actions button skeleton */}
                <Box h="12" w="140px" bg="gray.200" rounded="md" className="animate-pulse" />
                {/* Create Link button skeleton */}
                <Box h="12" w="140px" bg="blue.200" rounded="md" className="animate-pulse" />
              </HStack>
            </Flex>

            {/* AG-Grid Table Skeleton */}
            <Box
              bg="white"
              rounded="xl"
              shadow="sm"
              border="1px"
              borderColor="gray.200"
              overflow="hidden"
              h="600px"
            >
              {/* Table Header */}
              <Box p={4} borderBottom="2px" borderColor="gray.200" bg="gray.50">
                <HStack gap={4}>
                  {/* Column headers */}
                  {["Platform", "Campaign Name", "Ad Set", "Ad Name", "Path", "Actions"].map((_, i) => (
                    <Box key={i} flex={i === 1 ? 2 : 1}>
                      <Box h="4" w="80%" bg="gray.300" rounded="sm" className="animate-pulse" />
                    </Box>
                  ))}
                </HStack>
              </Box>
              
              {/* Table Rows */}
              <VStack align="stretch" p={0} gap={0}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                  <Box
                    key={row}
                    p={4}
                    borderBottom="1px"
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                  >
                    <HStack gap={4}>
                      {[1, 2, 3, 4, 5, 6].map((col) => (
                        <Box key={col} flex={col === 2 ? 2 : 1}>
                          <Box 
                            h="5" 
                            w={`${Math.random() * 30 + 50}%`} 
                            bg="gray.200" 
                            rounded="sm" 
                            className="animate-pulse" 
                          />
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxW="7xl" py={8}>
        <Box
          maxW="md"
          mx="auto"
          textAlign="center"
          p={8}
          bg="white"
          rounded="2xl"
          shadow="xl"
          border="1px"
          borderColor="red.100"
        >
          <Box
            w={16}
            h={16}
            bg="red.100"
            rounded="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
            mb={6}
          >
            <Icon as={FiLink} boxSize={8} color="red.500" />
          </Box>
          <Heading size="lg" color="gray.800" mb={2}>
            Something went wrong.
          </Heading>
          <Text color="gray.600" mb={6}>
            {error.message || "We couldn't load your links. Try again."}
          </Text>
          <HStack gap={3} justify="center">
            <Button
              onClick={() => refetch()}
              colorScheme="blue"
              size="lg"
              _hover={{ transform: "translateY(-2px)" }}
            >
              <Icon as={FiActivity} mr={2} />
              Try Again
            </Button>
            <Link to="/links/create">
              <Button variant="outline" colorScheme="blue" size="lg">
                <Icon as={FiPlus} mr={2} />
                Create New Link
              </Button>
            </Link>
          </HStack>
        </Box>
      </Container>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="7xl" py={8}>
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Box />
            <HStack gap={3}>
              {selectedRows.length > 1 && (
                <>
                  <Badge
                    colorScheme="blue"
                    px={3}
                    py={2}
                    rounded="full"
                    fontSize="sm"
                  >
                    {selectedRows.length} selected
                  </Badge>
                  <Box position="relative">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setShowBulkMenu(!showBulkMenu)}
                    >
                      Bulk Actions <Icon as={FiChevronDown} ml={2} />
                    </Button>
                    {showBulkMenu && (
                      <Box
                        position="absolute"
                        top="100%"
                        right={0}
                        mt={2}
                        bg="white"
                        border="1px"
                        borderColor="gray.200"
                        rounded="md"
                        shadow="lg"
                        minW="200px"
                        zIndex={10}
                      >
                        <VStack align="stretch" p={1}>
                          <Button
                            variant="ghost"
                            size="md"
                            w="full"
                            justifyContent="flex-start"
                            onClick={() => {
                              setShowBulkMenu(false);
                              navigate("/links/bulk-create");
                            }}
                          >
                            <Icon as={FiPackage} mr={2} /> Bulk Create New
                          </Button>
                          <Button
                            variant="ghost"
                            size="md"
                            w="full"
                            justifyContent="flex-start"
                            onClick={() => {
                              setShowBulkMenu(false);
                              // Pass selected IDs to the bulk update page
                              const selectedIds = selectedRows.map((row) => row.customId || row.id).filter(Boolean);
                              navigate("/links/bulk-update", { state: { selectedIds } });
                            }}
                          >
                            <Icon as={FiRefreshCw} mr={2} /> Bulk Update Selected
                          </Button>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {/* Bulk Actions Dropdown */}
              <Box position="relative">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowMainBulkMenu(!showMainBulkMenu)}
                  _hover={{ transform: "translateY(-2px)" }}
                >
                  Bulk Actions <Icon as={FiChevronDown} ml={2} />
                </Button>
                {showMainBulkMenu && (
                  <Box
                    position="absolute"
                    top="100%"
                    right={0}
                    mt={2}
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                    rounded="md"
                    shadow="lg"
                    minW="200px"
                    zIndex={10}
                  >
                    <VStack align="stretch" p={1}>
                      <Button
                        variant="ghost"
                        size="md"
                        w="full"
                        justifyContent="flex-start"
                        onClick={() => {
                          setShowMainBulkMenu(false);
                          navigate("/links/bulk-create");
                        }}
                      >
                        <Icon as={FiPackage} mr={2} /> Bulk Create
                      </Button>
                      <Button
                        variant="ghost"
                        size="md"
                        w="full"
                        justifyContent="flex-start"
                        onClick={() => {
                          setShowMainBulkMenu(false);
                          navigate("/links/bulk-update");
                        }}
                      >
                        <Icon as={FiRefreshCw} mr={2} /> Bulk Update
                      </Button>
                    </VStack>
                  </Box>
                )}
              </Box>
              <Link to="/links/create">
                <Button
                  size="lg"
                  colorScheme="blue"
                  _hover={{ transform: "translateY(-2px)" }}
                >
                  <Icon as={FiPlus} mr={2} />
                  Create Link
                </Button>{" "}
              </Link>
            </HStack>
          </Flex>

          {links.length === 0 ? (
            <Box
              bg="white"
              rounded="2xl"
              shadow="xl"
              p={12}
              textAlign="center"
              border="2px dashed"
              borderColor="gray.300"
            >
              <Box
                w={20}
                h={20}
                bg="blue.50"
                rounded="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mx="auto"
                mb={6}
              >
                <Icon as={RiAdvertisementLine} boxSize={10} color="blue.500" />
              </Box>
              <Heading size="lg" color="gray.800" mb={3}>
                Ready to begin?
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={8} maxW="md" mx="auto">
                Create your first link. See what's possible.
              </Text>
              <VStack gap={4} maxW="sm" mx="auto">
                <Link to="/links/create" style={{ width: "100%" }}>
                  <Button
                    size="lg"
                    colorScheme="blue"
                    width="full"
                    _hover={{ transform: "translateY(-2px)" }}
                  >
                    <Icon as={FiPlus} mr={2} />
                    Get Started
                  </Button>
                </Link>
                <HStack gap={6} color="gray.500" fontSize="sm">
                  <HStack gap={1}>
                    <Icon as={FiActivity} />
                    <Text>Track Clicks</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Icon as={FiTrendingUp} />
                    <Text>Monitor CTR</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Icon as={FiExternalLink} />
                    <Text>Multi-Platform</Text>
                  </HStack>
                </HStack>
              </VStack>
            </Box>
          ) : (
            <>
              {/* Links Table */}
              <Box
                bg="white"
                rounded="2xl"
                shadow="xl"
                overflow="hidden"
                border="1px"
                borderColor="gray.200"
              >
                <Box
                  p={4}
                  borderBottom="2px"
                  borderColor="gray.100"
                  bg="gray.50"
                >
                  <HStack justify="space-between">
                    <HStack gap={3}>
                      <Icon as={FiLink} boxSize={5} color="gray.600" />
                      <Heading size="md" color="gray.700">
                        All Links
                      </Heading>
                    </HStack>
                    <Badge colorScheme="blue" px={3} py={1} rounded="full">
                      {links.length} Total
                    </Badge>
                  </HStack>
                </Box>
                <Box height="600px" width="100%">
                  <AgGridReact
                    ref={gridRef}
                    rowData={links}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                    onRowDoubleClicked={(params) => {
                      if (params.data?.id) {
                        navigate(`/links/${params.data.id}/edit`);
                      }
                    }}
                    getRowStyle={() => ({
                      cursor: "pointer"
                    })}
                    onFirstDataRendered={(params) => {
                      // Auto-size columns when data is first rendered
                      params.api.autoSizeAllColumns();
                    }}
                    onGridSizeChanged={(params) => {
                      // Auto-size columns when grid is resized
                      params.api.autoSizeAllColumns();
                    }}
                    animateRows={true}
                    enableCharts={true}
                    sideBar={{
                      toolPanels: [
                        {
                          id: "columns",
                          labelDefault: "Columns",
                          labelKey: "columns",
                          iconKey: "columns",
                          toolPanel: "agColumnsToolPanel"
                        },
                        {
                          id: "filters",
                          labelDefault: "Filters",
                          labelKey: "filters",
                          iconKey: "filter",
                          toolPanel: "agFiltersToolPanel"
                        }
                      ],
                      hiddenByDefault: true
                    }}
                    rowSelection={{
                      mode: "multiRow",
                      checkboxes: true,
                      enableClickSelection: false,
                      headerCheckbox: true
                    }}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={[5, 10, 20, 50]}
                    paginationAutoPageSize={false}
                    suppressPaginationPanel={false}
                    paginationNumberFormatter={(params) => {
                      return params.value.toLocaleString();
                    }}
                    localeText={{
                      page: "Page",
                      more: "More",
                      to: "to",
                      of: "of",
                      next: "Next",
                      last: "Last",
                      first: "First",
                      previous: "Previous",
                      loadingOoo: "Loading...",
                      noRowsToShow: "No links to show"
                    }}
                    rowHeight={35}
                    loading={false}
                  />
                </Box>
              </Box>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
}

export default Links;
